<template>
  <v-container
    id="app-ticket"
    class="bloc-action-full pa-1"
    :style="isModal ? 'padding-top: 62px' : ''"
  >
    <v-card
      v-if="solde !== ''"
      class="mb-2"
    >
      <v-card-text class="text-center px-1 py-3 title white--text green">
        {{ $t('success.operation') }}
        <br>
        {{ $t('success.new_solde') }} : <span class="display-1">{{ solde }}</span>
      </v-card-text>
    </v-card>
    <v-card
      v-if="signature"
      class="mb-2"
    >
      <v-card-text class="text-center px-1 py-3 title white--text warning">
        {{ $t('success.ask_signature') }}
      </v-card-text>
    </v-card>
    <v-card
      v-if="leftToPay > 0"
      class="mb-2"
      color="success"
    >
      <v-card-text class="text-center px-1 py-3 title">
        {{ $t('words.left_to_pay') + ': ' + $amount(leftToPay) }}
      </v-card-text>
    </v-card>

    <!-- ************************
    ********** TICKETS **********
    ************************* -->
    <div ref="ticket">
      <div v-if="tickets !== null">
        <v-card
          v-for="(ticket, i) in tickets"
          :key="'ticket' + i"
          light
          class="mb-3"
        >
          <div class="pa-4">
            <img
              v-if="ticket.Image"
              :src="`${ ticket.isPNG ? 'data:image/png;base64,':'data:image/jpg;base64,'}${ticket.Image}`"
              alt="image"
              height="280px"
              class="mb-3 text-center"
            >
            <pre
              v-for="(line, index) in ticket.Lignes"
              :key="index"
              :class="{
                'text-left': line.Align === 'L',
                'text-right': line.Align === 'R',
                'text-center': line.Align === 'C',
                'title': line.Taille === 3,
                'font-01': line.Font === 1,
                'text-decoration-underline': line.Souligne === 1,
                'font-weight-bold': line.Gras === 1
              }"
              class="mb-1"
            >{{ line.Msg }}</pre>
            <!-- 'text-pre': line.Font === 1 -->
          </div>
        </v-card>
      </div>

      <v-card
        v-if="qrCodeUrl"
        light
        class="text-center"
      >
        <img
          :src="qrCodeUrl"
          alt="QR Code"
          style="width: 50%;"
        >
      </v-card>
    </div>

    <!-- *********************************
    ********** BOUTONS D'ACTION **********
    ********************************** -->
    <v-container
      class="bloc pull-bottom"
      fluid
    >
      <v-row class="pa-1">
        <v-col
          cols="12"
          md="6"
          offset-md="3"
          lg="4"
          offset-lg="4"
        >
          <v-container fluid>
            <v-row>
              <v-col
                cols="6"
                class="pr-1"
              >
                <v-btn
                  name="e-ticket"
                  block
                  large
                  @click="sendByMail()"
                >
                  {{ $t('button.ticket.e_ticket') }}
                </v-btn>
              </v-col>

              <v-col
                cols="6"
                class="pl-1"
              >
                <printer
                  class="h-bloc"
                  :tickets="ticketsToPrint"
                  :start-printing="startPrint"
                  use-activator
                  @send-by-mail="sendByMail()"
                />
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col
                v-show="showUseClient"
                cols="6"
                class="pr-1"
              >
                <v-btn
                  name="client"
                  block
                  large
                  :disabled="!useClient"
                  @click="toClient"
                >
                  {{ $t('button.ticket.customer_card') }}
                </v-btn>
              </v-col>
              <v-col
                :cols="showUseClient ? 6 : 12"
                :class="{ 'pl-1': showUseClient }"
              >
                <v-btn
                  name="valid"
                  block
                  large
                  color="success"
                  @click="close"
                >
                  {{ $t(`button.ticket.${externalAction ? 'back_from_external' : (isModal ? 'back' : 'home')}`) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
    </v-container>

    <!-- ****************************
    ********** MAIL DIALOG **********
    ***************************** -->
    <v-dialog
      v-model="showMail"
      persistent
      :content-class="dialogClass"
    >
      <v-card class="px-3">
        <div class="v-toolbar__content pa-0">
          <span class="title">Email client</span>
          <v-spacer />
          <v-btn
            icon
            @click="showMail = false; currentMail = ''"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </div>
        <v-divider />
        <v-text-field
          ref="user"
          v-model="currentMail"
          prepend-inner-icon="mail"
          :label="$t('field.index.user')"
          name="eticketmail"
          type="email"
          class="needsclick"
          @focus="dialogClass = 'dialog-top'"
          @blur="dialogClass = ''"
        />
        <v-btn
          class="bloc ma-0 my-3"
          :color="carrier === null ? 'success' : 'default'"
          :disabled="!mailValid"
          @click="sendByMail(currentMail)"
        >
          {{ $t('button.ticket.send') }}
        </v-btn>
        <v-btn
          v-show="carrier !== null"
          color="success"
          class="bloc ma-0 mb-3"
          large
          :disabled="!mailValid"
          @click="sendByMail(currentMail, true)"
        >
          {{ $t('button.ticket.send') }} {{ $t('button.ticket.and_maj') }}
        </v-btn>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Printer from '@/components/Printer'

import { Carrier } from '@/modules/Carrier'
import { $alert, $amount } from '@/modules/Utils'
import QRCode from 'qrcode'
import {MetierLocalStorage} from 'modulelocalstorage';

export default {
  name: 'Ticket',

  components: {
    Printer
  },

  props: {
    isModal: { type: Boolean, default: false },
    useListener: { type: Boolean, default: true },
    //
    tickets: { type: Array, default: null },
    startPrint: { type: Boolean, default: false },
    solde: { type: String, default: '' },
    leftToPay: { type: Number, default: 0 },
    carrier: { type: Object, default: null },
    forMerchant: { type: Boolean, default: false },
    signature: { type: Boolean, default: false },
    reference: { type: String, default: null },
    idBadge: { type: String, default: null },
    floatingModeEnabled: { type: Boolean, default: false },
  },

  data () {
    return {
      currentMail: '',
      showMail: false,
      dialogClass: '',
      qrCodeUrl: null,
    }
  },

  computed: {
    useClient () { return this.$store.state.drawerOptions.search === 1 },
    showUseClient () { return this.useClient && this.carrier !== null },
    externalAction () { return this.$store.getters.externalAction },
    //
    mailValid () { return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/.test(this.currentMail) },
    qrCode () { return this.$store.state.qrCodeUrl },
    ticketsToPrint () {
      return this.qrCodeUrl === null ? this.tickets : [...this.tickets, {
        Image: this.qrCodeUrl, Lignes: [
          { Msg: this.qrCode.text, Taille: 2, Align: 'C' },
          { Msg: this.qrCode.textSup, Taille: 2, Align: 'C' },
          { Msg: this.qrCode.textRes, Taille: 2, Align: 'C', Gras: 1 }
        ]
      }]
    },
    isModeFloating() {
      let mode = this.$store.state.teleparameters.CashierEngagementMode;
      return (mode === 'FLOATING') && this.$store.state.teleparameters.Cashiers.length > 0
    },
    isConnectedCashier() {
      return MetierLocalStorage.getConnectedCashier()
    }
  },

  beforeMount () {
    if ((!this.tickets || this.tickets.length === 0) && !this.qrCode) {
      $alert(this.$i18n.t('error.no_ticket'))
      this.close()
    } else {
      // Définition titre status bar
      this.$emit('title', this.$i18n.t('title.ticket'))
    }
  },

  mounted () {
    if (IS_DESKTOP && this.useListener) {
      window.addEventListener('keyup', this.keyup, false)
    }

    if (this.qrCode) {
      this.generateQRCode()
    }
  },

  beforeDestroy () {
    if (IS_DESKTOP && this.useListener) {
      window.removeEventListener('keyup', this.keyup, false)
    }
    this.$store.commit('qrCodeUrl', null)
  },

  methods: {
    $amount,
    keyup (e) {
      if (e.key === 'Enter') this.close()
    },
    close () {
      // Si on a un intent en mémoire, on retourne OK
      if (this.externalAction) {
        // Formatage des tickets client et marchand
        let customerTicket = ''
        let merchantTicket = ''
        this.tickets.forEach(ticket => {
          ticket.Lignes.forEach(line => {
            if (this.forMerchant || ticket.Marchand) {
              merchantTicket += (line.Msg + '\n')
            } else {
              customerTicket += (line.Msg + '\n')
            }
          })
        })

        CissAndroid.sendSuccess({
          initialAmount: this.$store.state.amount,
          payedAmount: this.$store.state.amount - this.leftToPay,
          leftToPay: this.leftToPay,
          customerTicket: customerTicket,
          merchantTicket: merchantTicket
        })

        return
      }
      // Si un caissier est connecté et que son mode d'engagement est Floating
      if (this.floatingModeEnabled) {
        this.finSession()
        return;
      }
      if (this.isModal) {
        this.$emit('hide')
      } else {
        this.$router.replace('/home')
      }
    },
    //
    sendByMail (mail = '', updateContact = false) {
      let carrier = this.carrier
      if (!this.forMerchant && (!carrier || !carrier.Mail || carrier.Mail === '')) {
        if (mail === '') {
          this.showMail = true
          return
        } else {
          carrier = {
            IDContact: 0,
            Nom: '',
            Prenom: '',
            Mail: mail.toLowerCase()
          }

          this.currentMail = ''
          this.showMail = false
        }
      }

      let lines = []
      this.tickets.forEach((ticket) => {
        // Si pour marchand, on envoie tous les tickets
        // Sinon (pour client) on vérifie que ce n'est pas un ticket marchand
        if (this.forMerchant || (!this.forMerchant && !ticket.Marchand)) {
          // Ajout des lignes
          lines = lines.concat(ticket.Lignes)
          // Ajout séparateur
          lines.push({ Msg: ' ', Taille: 2, Align: 'L' })
          lines.push({ Msg: '-------------------------', Taille: 2, Align: 'C' })
          lines.push({ Msg: ' ', Taille: 2, Align: 'L' })
        }
      })

      // Suppression dernier séparateur
      lines.splice(lines.length - 3, 3)

      Carrier.sendByMail(
        '[e-Ticket] ' + this.$store.state.merchantName + ' - Merci de votre visite',
        lines,
        carrier, // nullable (to merchant)
        this.idBadge,
        () => {
          $alert(this.$i18n.t('success.ticket_sent'), 'success')

          // Si email envoyé OK, on maj le contact
          if (updateContact) {
            setTimeout(() => {
              Carrier.setContact(
                () => {
                  this.carrier.Mail = mail
                },
                (error) => {
                  $alert(error)
                },
                this.carrier.IDContact,
                null,
                null,
                null,
                [{ Tag: 'MYTM', Email: mail, default: 1 }]
              )
            }, 1000)
          }
        },
        (error) => {
          $alert(error)
        }
      )
    },
    toClient () {
      this.$router.replace({
        name: 'customer',
        params: { customer: this.carrier, tickets: this.tickets }
      })
    },
    generateQRCode () {
      QRCode.toDataURL(this.qrCode.code)
        .then(url => {
          this.qrCodeUrl = url
        })
    },
    finSession () {
      localStorage.removeItem('connectedCashier')
      this.$router.replace('/')
    }
  }
}
</script>

<style scoped>
button {
  height: 50px;
}
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: #000000 !important;
}
.font-01{
  font-size: 10px;
  letter-spacing: 0.04em;
}
</style>
